import { StandardResponse } from "./functions";
import { IUnifiedFilter } from "./filters";

/**
 * Strict lowercase enumeration of all supported activity actions.
 */
export type ActivityAction =
  | "create"
  | "update"
  | "delete"
  | "logon"
  | "signout"
  | "signup"
  | "terminate_account"
  | "invite_contact";

/**
 * Strict lowercase enumeration of all supported entity and event target categories.
 */
export type ActivityTargetType =
  | "memory"
  | "media"
  | "contact"
  | "group"
  | "share"
  | "message"
  | "event"
  | "auth"
  | "user";

/**
 * Raw normalized database schema for an activity log document stored in Firestore collection `activityLogs`.
 */
export interface IActivityLog {
  /** Optional Firestore document ID */
  id?: string;
  /** AppUser canonical userId of the user who performed or triggered the action */
  userId: string;
  /** Normalized lowercase action identifier */
  action: ActivityAction;
  /** Normalized lowercase entity type */
  targetType: ActivityTargetType;
  /** Canonical foreign key ID of the target resource or user */
  targetId: string;
  /** Descriptive title or label of the target object at the time the action occurred */
  targetTitle?: string;
  /** Human-readable description of the activity event */
  description: string;
  /** Optional contextual metadata or field-level attributes related to the event */
  details?: Record<string, any>;
  /**
   * Audience list containing userIds of users who have permission to view this activity entry
   * (e.g. owners, editors, viewers of the target entity).
   */
  audienceUserIds: string[];
  /** Epoch millisecond timestamp when the activity occurred */
  createdAt: number;
}

/**
 * Hydrated presentation view model for an activity log entry consumed across frontend clients.
 */
export type ActivityLogModel = IActivityLog & {
  /** Display name of the actor who performed the action */
  actorDisplayName?: string;
  /** Email address of the actor */
  actorEmail?: string;
  /** Profile picture or avatar URL of the actor */
  actorAvatarUrl?: string;
  /** Client-side route for navigating directly to the referenced entity */
  targetRoute?: string;
  /** Computed flag indicating whether the logged-on user performed this action */
  isOwnActivity?: boolean;
};

/**
 * Request payload for fetching the authenticated user's activity stream.
 */
export interface IGetMyActivityRequest {
  /** Maximum number of activity records to return (defaults to 50, maximum 100) */
  limit?: number;
  /** Epoch timestamp to paginate after */
  startAfter?: number;
  /** Optional filter by target entity type */
  targetType?: ActivityTargetType;
  /** Optional filter by activity action */
  action?: ActivityAction;
  /** Optional unified cross-entity filter criteria */
  filter?: IUnifiedFilter;
}

/**
 * Response payload returning the authenticated user's activity stream.
 */
export interface IGetMyActivityResponse extends StandardResponse {
  /** List of hydrated activity log view models */
  activities: ActivityLogModel[];
  /** Flag indicating whether additional historical records are available */
  hasMore?: boolean;
  /** Oldest timestamp present in the returned batch for cursor pagination */
  lastTimestamp?: number;
}

/**
 * Request payload for administrator inspection of platform-wide activity logs.
 */
export interface IGetAllActivityRequest {
  /** Maximum number of activity records to return (defaults to 50, maximum 100) */
  limit?: number;
  /** Epoch timestamp to paginate after */
  startAfter?: number;
  /** Optional filter by target entity type */
  targetType?: ActivityTargetType;
  /** Optional filter by activity action */
  action?: ActivityAction;
  /** Optional filter by specific actor userId */
  userId?: string;
  /** Optional unified cross-entity filter criteria */
  filter?: IUnifiedFilter;
}

export type IGetAllActivityAdminRequest = IGetAllActivityRequest;

/**
 * Response payload returning platform-wide activity logs for administrators.
 */
export interface IGetAllActivityResponse extends StandardResponse {
  /** List of hydrated activity log view models */
  activities: ActivityLogModel[];
  /** Flag indicating whether additional historical records are available */
  hasMore?: boolean;
  /** Oldest timestamp present in the returned batch for cursor pagination */
  lastTimestamp?: number;
}

export type IGetAllActivityAdminResponse = IGetAllActivityResponse;

/**
 * Request payload to record a sign-out event.
 */
export interface IRecordSignOutActivityRequest {
  /** Optional client context */
  clientContext?: string;
}

/**
 * Response payload after recording a sign-out event.
 */
export interface IRecordSignOutActivityResponse extends StandardResponse {
  /** Status indicating whether the activity entry was successfully logged */
  logged: boolean;
}
